import { useCallback, useMemo, useState } from "react";
import reportsService from "../../services/surveyApi/reports.service";
import { IDateRange } from "./Reports";
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
import { FormatDate } from "../../utils/conversion";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  IReportColumn,
  exportToExcel,
} from "../../hooks/reports/useReportExport";
import {
  Select,
  TableColumns,
  TableColumnWidth,
  TableData,
  TablePreferences,
} from "@veneer/core";

// Define the type for each item in the select options
interface ISelectOption {
  value: number;
  label: string;
}

export type ReportPhase = "initial" | "columns" | "preview";

const PAGE_SIZE_DEFAULT = 10;
const PREFERENCE_TYPE_REPORT_COLUMNS = "REPORT_COLUMNS";

export default function ReportsLogic() {
  const [dataReportList, setDataReportList] = useState([]);
  const [dataReportListFiltered, setDataReportListFiltered] = useState([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [columnsLoading, setColumnsLoading] = useState<boolean>(false);
  const [exportLoading, setExportLoading] = useState<boolean>(false);
  const [reportmessage, setReportMessage] = useState<string>("");
  const languageID: any = useSelector((state: RootState) => state.languageId);
  const [t] = useTranslation("global");

  const [phase, setPhase] = useState<ReportPhase>("initial");
  const [availableColumns, setAvailableColumns] = useState<IReportColumn[]>([]);
  const [selectedColumnIndices, setSelectedColumnIndices] = useState<number[]>([]);
  const [previewData, setPreviewData] = useState<Record<string, any>[]>([]);
  const [fullPreviewData, setFullPreviewData] = useState<Record<string, any>[]>([]); // All columns data from API
  const [cachedDateRange, setCachedDateRange] = useState<IDateRange | null>(null); // Track what dates data was fetched for
  const [filteredPreviewData, setFilteredPreviewData] = useState<Record<string, any>[]>([]);
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_DEFAULT);
  const [reportFileName, setReportFileName] = useState<string>("");
  const [globalSearch, setGlobalSearch] = useState<string>("");

  const monthsLabel: string[] = useMemo(() => {
    return [
      t("header.january"),
      t("header.february"),
      t("header.march"),
      t("header.april"),
      t("header.may"),
      t("header.june"),
      t("header.july"),
      t("header.august"),
      t("header.september"),
      t("header.october"),
      t("header.november"),
      t("header.december"),
    ];
  }, [t]);

  // ── Step 1: user selects report type → fetch column metadata only ─────────
  // No data, no dates required — instant response from the backend.
  const loadColumns = async (sp: string, reportLabel: string) => {
    setReportMessage("");
    setColumnsLoading(true);
    
    if (sp === "GetWeeklyCapturesReport") {
      setAvailableColumns([]);
      setSelectedColumnIndices([]);
      setPreviewData([]);
      setFullPreviewData([]);
      setCachedDateRange(null);
      setFilteredPreviewData([]);
      setColumnFilters({});
      setGlobalSearch("");
      setCurrentPage(1);
      const nameExcel = reportLabel.trim().replace(/\s+/g, "_");
      setReportFileName(nameExcel);
      setPhase("columns");
      setColumnsLoading(false);
      return;
    }
    try {
      const result = await reportsService.getReportColumnsEndPoint(sp, languageID?.value);
      if (result && Array.isArray(result)) {
        const cols: IReportColumn[] = result.map((c: any) => ({
          id: c.propertyName,
          label: c.displayLabel,
        }));
        setAvailableColumns(cols);
        
        // Try to load user preferences for this report
        try {
          const savedPreference = await reportsService.getUserPreference(
            PREFERENCE_TYPE_REPORT_COLUMNS,
            sp
          );
          
          let savedColumnIds: string[] = [];
          
          // Handle both string (needs parsing) and array (already parsed) responses
          if (savedPreference) {
            if (typeof savedPreference === "string") {
              savedColumnIds = JSON.parse(savedPreference);
            } else if (Array.isArray(savedPreference)) {
              savedColumnIds = savedPreference;
            }
          }
          
          if (savedColumnIds.length > 0) {
            // Map saved column IDs to indices
            const indices = savedColumnIds
              .map((colId) => cols.findIndex((c) => c.id === colId))
              .filter((idx) => idx !== -1);
            setSelectedColumnIndices(indices.length > 0 ? indices : cols.map((_, i) => i));
          } else {
            // No preferences saved — select all columns by default
            setSelectedColumnIndices(cols.map((_, i) => i));
          }
        } catch {
          // Error loading preferences — select all columns by default
          setSelectedColumnIndices(cols.map((_, i) => i));
        }
        
        setPreviewData([]);
        setFullPreviewData([]);
        setCachedDateRange(null);
        setFilteredPreviewData([]);
        setColumnFilters({});
        setGlobalSearch("");
        setCurrentPage(1);
        const nameExcel = reportLabel.trim().replace(/\s+/g, "_");
        setReportFileName(nameExcel);
        setPhase("columns");
      } else {
        setReportMessage("donwload_failed");
      }
    } catch {
      setReportMessage("donwload_failed");
    } finally {
      setColumnsLoading(false);
    }
  };

  // ── Step 2: user picks columns + enters dates → Generate Preview ──────────
  // Fetch ALL columns from backend once, then filter client-side for performance.
  const loadPreviewData = (
    { value, sp }: { value: string; sp: string },
    date: IDateRange
  ) => {
    setReportMessage("");

    // Don't send selectedCols — fetch ALL columns, filter client-side
    const selectedCols: string[] = []; // Empty = backend returns all columns

    if (sp === "GetWeeklyCapturesReport") {
      if (!date?.startDate) {
        setReportMessage("donwload_failed");
        return;
      }
      const weekStart = moment(date.startDate).clone();
      const weekEnd = weekStart.clone().add(6, "days");
      const newWeekDate = FormatDate(weekStart, "MM/DD/YYYY");
      const weekRangeSuffix = `${weekStart.format("YYYYMMDD")}_${weekEnd.format(
        "YYYYMMDD"
      )}`;
      setLoading(true);
      reportsService
        .getReportWeeklyEndPoint(sp, newWeekDate, languageID?.value)
        .then(async (r: any) => {
          try {
            if (r?.statusCode === 401) {
              setReportMessage("donwload_failed");
            } else if (r instanceof Blob) {
              fallbackDirectDownload(r, `${reportFileName}_${weekRangeSuffix}`);
            }
          } catch (error) {
            console.error("[Reports] parse error:", error);
            setReportMessage("donwload_failed");
          } finally {
            setLoading(false);
          }
        })
        .catch(() => {
          setReportMessage("donwload_failed");
          setLoading(false);
        });
      return;
    }

    const applyPreviewPayload = (payload: any, dateUsed: IDateRange) => {
      const rows = Array.isArray(payload?.rows) ? payload.rows : [];
      setFullPreviewData(rows); // Store full data for client-side filtering
      setCachedDateRange(dateUsed); // Track what dates this data is for
      setPreviewData(rows);
      setFilteredPreviewData(rows);
      setColumnFilters({});
      setCurrentPage(1);
      setPhase("preview");
    };

    if (sp === "GetMonthlyCapturesReport") {
      const monthDate = FormatDate(date?.startDate, "MM/DD/YYYY");
      setLoading(true);
      reportsService
        .getReportPreviewEndPoint(sp, languageID?.value, undefined, undefined, monthDate, selectedCols)
        .then((r: any) => {
          try {
            if (r?.statusCode === 401) {
              setReportMessage("donwload_failed");
            } else {
              applyPreviewPayload(r, date);
            }
          } catch (error) {
            console.error("[Reports] parse error:", error);
            setReportMessage("donwload_failed");
          } finally {
            setLoading(false);
          }
        })
        .catch(() => {
          setReportMessage("donwload_failed");
          setLoading(false);
        });
      return;
    }

    let newStartDate = FormatDate(date?.startDate, "DD/MM/YYYY");
    let newEndDate = FormatDate(date?.endDate, "DD/MM/YYYY");

    if (sp === "GetUsersReport") {
      newStartDate = "01/01/1990"; //Estas fechas no se usan pero las dejare por si en un futuro las necesitan. Se envian por ahora con una fecha cualquiera para que no falle la peticion
      newEndDate = "01/01/1990";
    }

    setLoading(true);
    reportsService
      .getReportPreviewEndPoint(sp, languageID?.value, newStartDate, newEndDate, undefined, selectedCols)
      .then((r: any) => {
        try {
          if (r?.statusCode === 401) {
            setReportMessage("donwload_failed");
          } else {
            applyPreviewPayload(r, date);
          }
        } catch (error) {
          console.error("[Reports] parse error:", error);
          setReportMessage("donwload_failed");
        } finally {
          setLoading(false);
        }
      })
      .catch(() => {
        setReportMessage("donwload_failed");
        setLoading(false);
      });
  };

  // ── Fallback: direct download (used when exceljs parsing fails) ───────────
  const fallbackDirectDownload = (blob: Blob, nameExcel: string) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${nameExcel}.xlsx`;
    document.body.appendChild(link);
    link.click();
    URL.revokeObjectURL(url);
    link.remove();
    setReportMessage("successfully_generated");
  };

  const getReportList = () => {
    reportsService.getReportListEndPoint(languageID?.value).then((r) => {
      try {
        if (r && r?.statusCode !== 401) {
          const data = r.map((item: any) => {
            return {
              value: item.fieldID,
              label: item.fieldValue,
              sp: item.fieldSP,
            };
          });
          setDataReportList(data);
          setDataReportListFiltered(data);
        }
      } catch (error) {
        console.error("Error al descargar el archivo:", error);
      }
    });
  };

  const handleChangeSearchReport = (value: string) => {
    const filterArray = dataReportList.filter((element: any) => {
      if (
        element.value
          .toString()
          .toLowerCase()
          .includes(String(value).toLowerCase()) ||
        element.label
          ?.toString()
          .toLowerCase()
          .includes(String(value).toLowerCase())
      ) {
        return element;
      }
    });
    setDataReportListFiltered(filterArray);
  };

  // ── Column selection ───────────────────────────────────────────────────────
  const handleToggleColumn = (index: number) => {
    setSelectedColumnIndices((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  };

  const handleClearColumns = () => setSelectedColumnIndices([]);

  const handleSelectAllColumns = () =>
    setSelectedColumnIndices(columnSelectOptions.map((opt) => opt.value));

  // ── Check if we can use cached data (same dates) ─────────────────────────
  const isSameDateRange = (date1: IDateRange | null, date2: IDateRange): boolean => {
    if (!date1) return false;
    const start1 = date1.startDate ? moment(date1.startDate).format("YYYY-MM-DD") : null;
    const start2 = date2.startDate ? moment(date2.startDate).format("YYYY-MM-DD") : null;
    const end1 = date1.endDate ? moment(date1.endDate).format("YYYY-MM-DD") : null;
    const end2 = date2.endDate ? moment(date2.endDate).format("YYYY-MM-DD") : null;
    return start1 === start2 && end1 === end2;
  };

  // ── Regenerate preview from cached data (no API call) ─────────────────────
  const regeneratePreviewFromCache = () => {
    if (fullPreviewData.length === 0) return;
    setPreviewData(fullPreviewData);
    setFilteredPreviewData(fullPreviewData);
    setColumnFilters({});
    setGlobalSearch("");
    setCurrentPage(1);
    setPhase("preview");
  };

  // ── Advance to preview (calls data API or uses cache) ─────────────────────
  const generatePreview = async (
    reportSelected: { value: string; sp: string },
    dateRange: IDateRange
  ) => {
    // Save column preferences when generating preview
    try {
      const selectedColumnIds = availableColumns
        .filter((_, i) => selectedColumnIndices.includes(i))
        .map((c) => c.id);
      
      const preferenceValue = JSON.stringify(selectedColumnIds);
      await reportsService.saveUserPreference(
        PREFERENCE_TYPE_REPORT_COLUMNS,
        reportSelected.sp,
        preferenceValue
      );
    } catch (error) {
      // Silently fail — preferences are a nice-to-have, not critical
      console.error("Failed to save column preferences:", error);
    }

    // If we have cached data for the same date range, use it (instant preview)
    if (fullPreviewData.length > 0 && isSameDateRange(cachedDateRange, dateRange)) {
      regeneratePreviewFromCache();
      return;
    }

    // Otherwise, fetch from API
    loadPreviewData(reportSelected, dateRange);
  };

  // ── Column-level filtering (client-side) ──────────────────────────────────
  const applyFilters = (filters: Record<string, string>, search: string) => {
    const filtered = previewData.filter((row) => {
      const passesColumnFilters = Object.entries(filters).every(
        ([key, filterVal]) => !filterVal || row[key]?.toString() === filterVal
      );
      const passesGlobalSearch =
        !search ||
        Object.values(row).some((val) =>
          val?.toString().toLowerCase().includes(search.toLowerCase())
        );
      return passesColumnFilters && passesGlobalSearch;
    });
    setFilteredPreviewData(filtered);
    setCurrentPage(1);
  };

  const handleColumnFilterChange = (colId: string, val: string) => {
    const newFilters = { ...columnFilters, [colId]: val };
    if (!val) delete newFilters[colId];
    setColumnFilters(newFilters);
    applyFilters(newFilters, globalSearch);
  };

  const handleGlobalSearchChange = (val: string) => {
    setGlobalSearch(val);
    applyFilters(columnFilters, val);
  };

  const clearAllFilters = useCallback(() => {
    setColumnFilters({});
    setGlobalSearch("");
    setFilteredPreviewData(previewData);
    setCurrentPage(1);
  }, [previewData]);

  // ── Export: all preview rows (selected columns) ───────────────────────────
  const handleExportExcel = async () => {
    const cols = availableColumns.filter((_, i) =>
      selectedColumnIndices.includes(i)
    );
    if (cols.length === 0 || previewData.length === 0) return;
    try {
      setExportLoading(true);
      await exportToExcel(cols, previewData, reportFileName);
    } finally {
      setExportLoading(false);
    }
  };

  // ── Export: only currently filtered rows (selected columns) ───────────────
  const handleExportFilteredExcel = async () => {
    const cols = availableColumns.filter((_, i) =>
      selectedColumnIndices.includes(i)
    );
    if (cols.length === 0 || filteredPreviewData.length === 0) return;
    try {
      setExportLoading(true);
      await exportToExcel(cols, filteredPreviewData, `${reportFileName}_filtered`);
    } finally {
      setExportLoading(false);
    }
  };

  // ── Navigation ────────────────────────────────────────────────────────────
  const resetToInitial = () => {
    setPhase("initial");
    setAvailableColumns([]);
    setSelectedColumnIndices([]);
    setPreviewData([]);
    setFullPreviewData([]);
    setCachedDateRange(null);
    setFilteredPreviewData([]);
    setColumnFilters({});
    setGlobalSearch("");
    setCurrentPage(1);
    setReportMessage("");
  };

  const goBackToColumns = () => {
    // Keep fullPreviewData and cachedDateRange so we can reuse if dates don't change
    setPhase("columns");
    setColumnFilters({});
    setGlobalSearch("");
    setCurrentPage(1);
  };

  // Check if we have cached data available for quick preview
  const hasCachedData = fullPreviewData.length > 0;

  // ── Derived / computed ────────────────────────────────────────────────────
  const selectedColumns = useMemo(
    () => availableColumns.filter((_, i) => selectedColumnIndices.includes(i)),
    [availableColumns, selectedColumnIndices]
  );

  const columnSelectOptions = useMemo(
    () => availableColumns.map((c, i) => ({ value: i, label: c.label })),
    [availableColumns]
  );

  const pagedData: TableData[] = useMemo(
    () =>
      filteredPreviewData.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
      ),
    [filteredPreviewData, currentPage, pageSize]
  );

  // Unique values per selected column based on the currently available dataset
  // (respecting global search + other active column filters).
  const allUniqueValues = useMemo((): Record<string, Array<{ value: string; label: string }>> => {
    const result: Record<string, Array<{ value: string; label: string }>> = {};

    const passesGlobalSearch = (row: Record<string, any>): boolean =>
      !globalSearch ||
      Object.values(row).some((val) =>
        val?.toString().toLowerCase().includes(globalSearch.toLowerCase())
      );

    selectedColumns.forEach((col) => {
      const seen = new Set<string>();
      const values: Array<{ value: string; label: string }> = [];

      // Apply all active filters except this column, so its dropdown shows
      // only values still available given current selections.
      const rowsForThisColumn = previewData.filter((row) => {
        if (!passesGlobalSearch(row)) return false;
        return Object.entries(columnFilters).every(([key, filterVal]) => {
          if (!filterVal) return true;
          if (key === col.id) return true;
          return row[key]?.toString() === filterVal;
        });
      });

      rowsForThisColumn.forEach((row) => {
        const val = row[col.id]?.toString();
        if (val !== undefined && val !== "" && !seen.has(val)) {
          seen.add(val);
          values.push({ value: val, label: val });
        }
      });

      result[col.id] = [...values].sort((a, b) => a.label.localeCompare(b.label));
    });

    return result;
  }, [previewData, selectedColumns, columnFilters, globalSearch]);

  const tableColumns: TableColumns[] = useMemo(
    () =>
      selectedColumns.map((col) => ({
        id: col.id,
        label: col.label,
        filter: (
          <Select
            id={`filter-col-${col.id}`}
            options={[
              { value: "", label: t("header.all") },
              ...(allUniqueValues[col.id] ?? []),
            ]}
            value={columnFilters[col.id] ? [columnFilters[col.id]] : [""]}
            onChange={(e: any) =>
              handleColumnFilterChange(col.id, String(e?.value ?? ""))
            }
            clearIcon={false}
            placement="bottom"
          />
        ),
      })),
    [selectedColumns, allUniqueValues, columnFilters, t]
  );

  const tablePreferences: TablePreferences = useMemo(() => {
    const equalWidth =
      selectedColumns.length > 0
        ? `${Math.floor(100 / selectedColumns.length)}%`
        : "auto";
    const widths: TableColumnWidth[] = selectedColumns.map((col) => ({
      columnId: col.id,
      width: equalWidth,
    }));
    return { width: widths };
  }, [selectedColumns]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (_event: any, option: any) => {
    setPageSize(option.value as number);
    setCurrentPage(1);
  };

  const tablePagination = {
    currentPage,
    onPageChange: handlePageChange,
    onPageSizeChange: handlePageSizeChange,
    pageSize,
    totalItems: filteredPreviewData.length,
  };

  // ── Year/month helpers ────────────────────────────────────────────────────
  const calculateYearsAvailablesToSelect = useCallback((): ISelectOption[] => {
    const yearStart = 2024;
    const currentYear = moment().year();
    const years: ISelectOption[] = [];
    for (let index = yearStart; index <= currentYear; index++) {
      years.push({
        value: index,
        label: index.toString(),
      });
    }

    return years;
  }, []);

  const calculateMonthsAvailablesToSelect = useCallback(
    (year: number | undefined): ISelectOption[] => {
      if (!year || year === 0) return [];

      const months: ISelectOption[] = [];
      let maxMonth: number = 11;

      if (year === moment().year()) {
        maxMonth = moment().month() - 1; // set as max month the before month
      }

      for (let index = 0; index <= maxMonth; index++) {
        months.push({
          value: index,
          label: monthsLabel[index],
        });
      }

      return months;
    },
    [monthsLabel]
  );

  return {
    // Report list
    getReportList,
    dataReportList,
    dataReportListFiltered,
    handleChangeSearchReport,
    calculateMonthsAvailablesToSelect,
    calculateYearsAvailablesToSelect,
    // Status
    loading,
    columnsLoading,
    exportLoading,
    reportmessage,
    // Phase
    phase,
    loadColumns,
    loadPreviewData,
    resetToInitial,
    goBackToColumns,
    // Cache status
    hasCachedData,
    cachedDateRange,
    // Column selection
    columnSelectOptions,
    selectedColumnIndices,
    handleToggleColumn,
    handleClearColumns,
    handleSelectAllColumns,
    generatePreview,
    // Preview table
    selectedColumns,
    columnFilters,
    handleColumnFilterChange,
    globalSearch,
    handleGlobalSearchChange,
    clearAllFilters,
    allUniqueValues,
    pagedData,
    tableColumns,
    tablePreferences,
    tablePagination,
    filteredPreviewData,
    // Export
    handleExportExcel,
    handleExportFilteredExcel,
    // Fallback direct download
    fallbackDirectDownload,
  };
}
